<script setup>
import { computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';

import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';

const props = defineProps({
  allowedRoles: { type: Array, default: () => [] },
  allowedAgentIds: { type: Array, default: () => [] },
});

const emit = defineEmits(['update:allowedRoles', 'update:allowedAgentIds']);

const { t } = useI18n();
const store = useStore();
const customRoles = useMapGetter('customRole/getCustomRoles');
const agents = useMapGetter('agents/getVerifiedAgents');

onMounted(() => {
  if (!customRoles.value.length) store.dispatch('customRole/getCustomRole');
  if (!agents.value.length) store.dispatch('agents/get');
});

// A custom role is addressed as `custom_<id>`, so both kinds of role live in the same list.
const roleOptions = computed(() => [
  {
    value: 'administrator',
    label: t('LABEL_MGMT.FORM.ACCESS.ROLES.ADMINISTRATOR'),
  },
  { value: 'agent', label: t('LABEL_MGMT.FORM.ACCESS.ROLES.AGENT') },
  ...customRoles.value.map(role => ({
    value: `custom_${role.id}`,
    label: role.name,
  })),
]);

const agentOptions = computed(() =>
  agents.value.map(agent => ({ value: agent.id, label: agent.name }))
);
</script>

<template>
  <div class="flex flex-col gap-3 w-full">
    <div class="flex flex-col gap-1">
      <label>{{ $t('LABEL_MGMT.FORM.ACCESS.ROLES.LABEL') }}</label>
      <TagMultiSelectComboBox
        :model-value="props.allowedRoles"
        :options="roleOptions"
        :placeholder="$t('LABEL_MGMT.FORM.ACCESS.ROLES.PLACEHOLDER')"
        @update:model-value="value => emit('update:allowedRoles', value)"
      />
    </div>
    <div class="flex flex-col gap-1">
      <label>{{ $t('LABEL_MGMT.FORM.ACCESS.AGENTS.LABEL') }}</label>
      <TagMultiSelectComboBox
        :model-value="props.allowedAgentIds"
        :options="agentOptions"
        :placeholder="$t('LABEL_MGMT.FORM.ACCESS.AGENTS.PLACEHOLDER')"
        @update:model-value="value => emit('update:allowedAgentIds', value)"
      />
    </div>
    <p class="mb-0 text-sm text-n-slate-11">
      {{ $t('LABEL_MGMT.FORM.ACCESS.HELP_TEXT') }}
    </p>
  </div>
</template>
